// Privacy Policy, Cookie Policy and cookie banner in English.
// Each section: { title, paragraphs?: [...], items?: [...] }. Texts support <b>…</b>.
const legal = {
  updated: "Last updated: October 21, 2026",
  backToTop: "Back to top",

  privacy: {
    title: "Privacy Policy",
    intro:
      "This notice explains how Qapex S.R.L. processes the personal data of users who visit qapex.it and use its services, pursuant to Articles 13 and 14 of Regulation (EU) 2016/679 (\"GDPR\") and Italian Legislative Decree 196/2003 as amended by Legislative Decree 101/2018.",
    sections: [
      {
        title: "1. Data controller",
        paragraphs: [
          "<b>Qapex S.R.L.</b> – VAT IT03818350542 – Via della Louvière, n. 1/A, 06034 Foligno (PG), Italy.",
          "For any request about your personal data, write to <b>info@qapex.it</b>.",
        ],
      },
      {
        title: "2. Data we process",
        items: [
          "<b>Browsing data</b>: IP address, browser and device type, pages visited, date and time of the request. It is collected automatically by the systems that run the website.",
          "<b>Data provided through the contact form</b>: first name, last name, email, phone number and the content of your message.",
          "<b>Job application data</b>: name, email, the position you apply for and your CV, including the data it contains.",
          "<b>Data collected through cookies and tracking tools</b>, only if you have given consent. See the Cookie Policy for details.",
        ],
      },
      {
        title: "3. Purposes and legal bases",
        items: [
          "<b>Replying to your contact and information requests</b>: pre-contractual measures taken at your request (Art. 6(1)(b) GDPR).",
          "<b>Assessing applications and recruiting</b>: pre-contractual measures at your request (Art. 6(1)(b)) and, for CV data, consent (Art. 6(1)(a) and Art. 9(2)(a) for any special categories of data).",
          "<b>Keeping the website running and secure</b>: the controller's legitimate interest (Art. 6(1)(f)).",
          "<b>Measuring website visits (Google Analytics) and the performance of advertising campaigns (Meta Pixel)</b>: consent (Art. 6(1)(a)), which you can withdraw at any time.",
          "<b>Complying with legal obligations and protecting the controller's rights</b>: legal obligation (Art. 6(1)(c)) and legitimate interest (Art. 6(1)(f)).",
        ],
      },
      {
        title: "4. Providing your data",
        paragraphs: [
          "Providing the data requested in the forms is optional, but without it we cannot reply to your request or assess your application. Consent to measurement and marketing cookies is optional and refusing it does not limit your browsing.",
        ],
      },
      {
        title: "5. How data is processed and recipients",
        paragraphs: [
          "Data is processed electronically with appropriate security measures. It may be accessed by the controller's authorised staff and by providers that deliver services on our behalf, appointed as data processors (Art. 28 GDPR), including:",
        ],
        items: [
          "the website hosting provider;",
          "Google LLC / Google Ireland Ltd (Google Workspace and Apps Script to receive contact form messages; Google Fonts for typefaces);",
          "FormSubmit (formsubmit.co) to forward job applications by email;",
          "Google Ireland Ltd (Google Analytics), only with your consent;",
          "Meta Platforms Ireland Ltd (Meta Pixel), only with your consent.",
        ],
      },
      {
        title: "6. Transfers outside the EU",
        paragraphs: [
          "Some of these providers may process data in the United States. In those cases the transfer relies on the EU-US Data Privacy Framework, where the provider is certified, or on the Standard Contractual Clauses approved by the European Commission.",
        ],
      },
      {
        title: "7. Retention",
        items: [
          "Contact requests: for as long as needed to handle the request and in any case no longer than 24 months after the last contact.",
          "Job applications: no longer than 24 months after receipt, unless you ask us to delete them sooner.",
          "Browsing data: for as long as strictly needed for website security, unless required to investigate unlawful activity.",
          "Data collected through cookies: for the durations listed in the Cookie Policy.",
        ],
      },
      {
        title: "8. Your rights",
        paragraphs: [
          "At any time you can request access to your data, rectification, erasure, restriction of processing and portability, and you can object to processing based on legitimate interest (Arts. 15–22 GDPR). You can withdraw consent at any time, without affecting the lawfulness of processing carried out before withdrawal.",
          "To exercise your rights, write to <b>info@qapex.it</b>. If you believe the processing breaches the GDPR, you can lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it) or with the supervisory authority of your country.",
        ],
      },
      {
        title: "9. Links to external websites",
        paragraphs: [
          "This website contains hyperlinks (links), i.e. tools that connect to web pages of other websites: the external websites reachable through links are developed and managed by parties over which Qapex Srl has no ownership or control, and Qapex Srl is in no way responsible for their content, quality, accuracy or the services they offer. Visiting and using the websites reached by the user from this website through links is therefore left entirely to the discretion and responsibility of the user.",
          "This notice is therefore provided only for the Qapex.it website and not for any other websites the user may visit through links.",
        ],
      },
      {
        title: "10. Changes",
        paragraphs: [
          "We may update this notice. The current version is always published on this page, with the date of the last update.",
        ],
      },
    ],
  },

  cookie: {
    title: "Cookie Policy",
    intro:
      "Cookies are small text files that a website stores on your device. This page explains which cookies and similar technologies (such as local storage) qapex.it uses and how you can manage them.",
    sections: [
      {
        title: "1. Technical tools (always active)",
        paragraphs: [
          "They are needed for the website to work and do not require consent.",
        ],
        items: [
          "<b>qapex_cookie_consent</b> (local storage, Qapex): stores your cookie choice. Duration: 12 months.",
          "<b>Google Fonts</b> (Google): serves the website's typefaces. It sets no cookies, but receives your browser's IP address in order to deliver the files.",
        ],
      },
      {
        title: "2. Measurement and marketing cookies (consent only)",
        paragraphs: [
          "They are enabled only if you click \"Accept\" in the banner.",
        ],
        items: [
          "<b>Google Analytics 4</b> (Google Ireland Ltd): measures, in aggregate form, visits to the website, pages viewed and where visitors come from. Cookies: _ga (2 years), _ga_D13SGH4CKN (2 years). Privacy policy: policies.google.com/privacy.",
          "<b>Meta Pixel</b> (Meta Platforms Ireland Ltd): measures visits coming from advertising campaigns on Facebook and Instagram and helps show relevant ads. Cookies: _fbp (3 months), fr (3 months). Privacy policy: www.facebook.com/privacy/policy.",
        ],
      },
      {
        title: "3. Managing your consent",
        paragraphs: [
          "On your first visit you can accept or refuse measurement and marketing cookies from the banner. You can change your mind at any time with the \"Cookie preferences\" button at the bottom of this page. You can also delete or block cookies in your browser settings.",
          "For everything else (controller, rights, contacts) see the Privacy Policy.",
        ],
      },
    ],
  },

  banner: {
    text:
      "We (qapex.it) and selected third parties (3) use cookies or similar technologies for technical purposes and, with your consent, also for <b>functionality, experience, measurement and “marketing (personalized ads)”</b> purposes as specified in the <link>cookie policy</link>.",
    text2:
      "You can freely give, deny or withdraw your consent at any time by accessing the preferences panel. Denying consent may make related features unavailable.",
    text3: "Use the “Accept” button to consent. Use the “Reject” button to continue without accepting.",
    customize: "Learn more and customize",
    necessaryTitle: "Necessary",
    necessaryText: "Tools the website needs to work, such as storing your cookie preferences.",
    alwaysOn: "Always active",
    marketingTitle: "Measurement and marketing",
    marketingText: "Google Analytics: measures website visits in aggregate form. Meta Pixel: measures visits coming from our Facebook and Instagram campaigns and helps show personalized ads.",
    save: "Save and continue",
    accept: "Accept",
    reject: "Reject",
    settings: "Cookie preferences",
  },
};

export default legal;
